import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { IMAGES } from "@/data/services";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import PricingSection from "@/components/PricingSection";
import Seo from "@/components/Seo";

const rollingPhrases = [
  "Residential Fall Cleanups",
  "Residential Snow Removal",
  "Residential Spring Cleanups",
  "Residential Landscaping Enhancements",
];

function RollingServiceBanner() {
  const row = [...rollingPhrases, ...rollingPhrases];
  return (
    <div className="overflow-hidden border-y border-[#043B1C]/10 bg-[#F4F6F3] py-5" data-testid="residential-rolling-banner">
      <div className="marquee-track flex w-max items-center gap-10">
        {row.map((phrase, i) => (
          <span key={i} className="flex items-center gap-10 whitespace-nowrap">
            <span className="text-sm font-bold uppercase tracking-[0.22em] text-[#043B1C]" data-testid={i === 0 ? "residential-rolling-phrase" : undefined}>
              {phrase}
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-[#F4BE36]" />
          </span>
        ))}
      </div>
    </div>
  );
}

const benefits = [
  "A dedicated crew who knows your property",
  "Predictable scheduling and billing",
  "Seasonal services that keep your property looking its best year round",
];

export default function Residential() {
  return (
    <div data-testid="residential-page">
      <Seo
        title="Residential Lawn Care Subscriptions, Legacy Scapes Ltd"
        description="Simple, predictable lawn care subscriptions for homeowners across the St. Clair Corridor. Sign up and let your crew handle the rest."
      />

      <section className="bg-[#F4F6F3] pt-[76px]">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>For homeowners</Eyebrow>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-[#16241C] sm:text-5xl" data-testid="residential-headline">
              Consistent Lawn Care, Without the Hassle
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1160px] items-center gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="overflow-hidden rounded-2xl">
              <img src={IMAGES.residential} alt="Legacy Scapes branded truck and trailer parked at a residential home" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <div className="space-y-4 text-base leading-[1.7] text-[#5B6B60]">
                <p>
                  Residential service with Legacy Scapes runs on a simple subscription model. Sign
                  up, choose the plan that fits your property, and your crew shows up on schedule,
                  every time. No guessing when someone's coming, just consistent care handled for you.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <RollingServiceBanner />

      <PricingSection />

      <section className="bg-[#F4F6F3]">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>The short version</Eyebrow>
            <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.2rem]">
              What homeowners get
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.08}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-[#16241C]/10 bg-white p-6" data-testid={`residential-benefit-${i}`}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#043B1C]">
                    <Check size={14} strokeWidth={3} className="text-[#F4BE36]" />
                  </span>
                  <p className="text-base font-bold leading-[1.5] text-[#16241C]">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand heading="Your Grounds, Handled Year Round" buttonText="Get Started" />
    </div>
  );
}
